import React, { useRef, useState } from "react";
import { Badge } from "../../../ForBrands/ui/BrandUI";
import { Icon } from "../../../ForBrands/ui/icons";
import { EMPTY, MAX_LOCK, fmtMoney2, fmtScore, rowId } from "../allocationModel";

// The values the roster tables (tablet and desktop) and the player cards
// (phones) both show, so a player reads the same in either layout.

export const POOLS = {
  rs: { flag: "hiddenRs", name: "revenue share", group: "Revenue share payout" },
  nil: { flag: "hiddenNil", name: "NIL", group: "NIL payout" },
};

// "Hide Isaiah Taylor from revenue share" / "Show Isaiah Taylor in NIL".
export const eyeLabel = (row, pool, out) => `${out ? "Show" : "Hide"} ${row.playerName} ${out ? "in" : "from"} ${POOLS[pool].name}`;

// Generated initials avatars (dicebear) are replaced by the V2 outline avatar.
export const photoOf = (url) => (url && !url.includes("dicebear.com") ? url : undefined);

export const isSet = (value) => value !== null && value !== undefined;

// Amounts carry a title, so one that still has to truncate reads on hover.
export const Money = ({ value }) => (
  <span title={isSet(value) ? fmtMoney2(value) : undefined} className={isSet(value) ? "text-bm-ink" : "text-bm-tertiary"}>
    {fmtMoney2(value)}
  </span>
);

// A typed amount that clears the one the field opened with or moves it by a
// cent or more.
const changed = (next, value) => (next === null ? isSet(value) : !isSet(value) || Math.round(next * 100) !== Math.round(Number(value) * 100));

// The small lock before a locked amount; clicking it unlocks the player. In
// a table it takes 14px of the cell, so a locked amount keeps the room it
// needs; on a card it is a 24px target.
export const LockButton = ({ name, onUnlock, disabled, card = false }) => (
  <button
    type="button"
    onClick={onUnlock}
    disabled={disabled}
    aria-label={`Unlock ${name}`}
    title="Locked at this amount. Click to unlock."
    className={`flex h-6 shrink-0 items-center justify-center text-bm-orange hover:text-[#e63d00] disabled:cursor-default ${card ? "w-6" : "w-3.5"}`}
  >
    <Icon name="lock" size={card ? 14 : 12} />
  </button>
);

// The box an Allocated amount sits in, the size of its field so nothing
// moves when the coach starts typing: 36px in a table (right-aligned), 40px
// on a card (left-aligned). Idle it is a white field with the V2 border,
// darker on hover and orange with the 3px ring on keyboard focus; typing
// keeps it orange with the ring; a hidden player's is transparent. (Class
// names stay whole so Tailwind finds them.)
const BOX = {
  table: "flex h-9 w-full min-w-0 items-center justify-end gap-0.5 rounded-[8px] border px-2",
  card: "flex h-10 w-full min-w-0 items-center gap-1 rounded-[8px] border px-3",
};
const BOX_IDLE = "border-bm-border bg-white hover:border-bm-strong has-[:focus-visible]:border-bm-orange has-[:focus-visible]:shadow-[0_0_0_3px_rgba(255,68,0,0.12)]";
const BOX_OFF = "border-transparent bg-transparent";
const BOX_TYPING = "border-bm-orange bg-white shadow-[0_0_0_3px_rgba(255,68,0,0.12)]";
// The amount fills the box, so a click anywhere in it opens the field; the
// box's ring shows its keyboard focus.
const AMOUNT = {
  table: "min-w-0 max-w-full flex-1 truncate bm-body-m text-right text-bm-ink outline-none disabled:cursor-default",
  card: "min-w-0 max-w-full flex-1 truncate bm-label-l text-left text-bm-ink outline-none disabled:cursor-default",
};

// An amount the coach can lock (Allocated, in revenue share and in NIL): a
// plain "-" until the split has one, then a button in a box that opens a
// field. Enter locks the player at the typed amount, even the amount shown;
// leaving the field locks only an amount moved by a cent or more from the
// one it opened with, so clicking in and away never locks anyone, even when
// a new split lands while the field is open. An empty field (or the lock
// button) unlocks. A hidden player's amount (`disabled`) sits in a
// transparent box. On a card (`card`) the amount is in the Label/L value
// style and the field has 16px text so iOS does not zoom into it. The lock
// button leads the amount and `trail` (the minimum's arrow) follows it,
// beside it in the box, never inside its button.
export const PayoutInput = ({ value, onCommit, disabled, card = false, label = "Allocated amount", locked = false, name = "", trail = null }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  // The amount the field opened with, what leaving it compares against.
  const opened = useRef(null);
  // Enter and Escape close the field; a blur as it closes changes nothing.
  const closed = useRef(false);
  const commit = (force) => {
    if (closed.current) return;
    closed.current = true;
    const cleaned = draft.replace(/[$,\s]/g, "");
    const next = cleaned === "" ? null : Number(cleaned);
    setEditing(false);
    if (next !== null && !(next >= 0 && next < MAX_LOCK)) return;
    if (force || changed(next, opened.current)) onCommit(next);
  };
  const shape = BOX[card ? "card" : "table"];
  if (!editing) {
    if (!isSet(value)) {
      // In a table the dash lines up with the amounts inside the boxes.
      if (!disabled) return <span className={card ? "text-bm-tertiary" : "pr-[9px] text-bm-tertiary"}>{EMPTY}</span>;
      return (
        <span className={`${shape} ${BOX_OFF}`}>
          <span className="text-bm-tertiary">{EMPTY}</span>
        </span>
      );
    }
    const amount = fmtMoney2(value);
    return (
      <span className={`${shape} ${disabled ? BOX_OFF : BOX_IDLE}`}>
        {locked && <LockButton name={name} card={card} onUnlock={() => onCommit(null)} disabled={disabled} />}
        <button
          type="button"
          title={amount}
          disabled={disabled}
          onClick={() => {
            closed.current = false;
            opened.current = value;
            setDraft(String(value));
            setEditing(true);
          }}
          className={AMOUNT[card ? "card" : "table"]}
        >
          {amount}
        </button>
        {trail}
      </span>
    );
  }
  return (
    <span className={`${shape} ${BOX_TYPING}`}>
      <span className={card ? "bm-body-m text-bm-muted" : "bm-body-s text-bm-muted"}>$</span>
      <input
        autoFocus
        value={draft}
        inputMode="decimal"
        aria-label={label}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => commit(false)}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit(true);
          if (e.key === "Escape") {
            closed.current = true;
            setEditing(false);
          }
        }}
        className={
          card
            ? "bm-bare min-w-0 flex-1 bg-transparent text-[16px] font-normal leading-[22px] text-bm-ink outline-none"
            : "bm-bare min-w-0 flex-1 bg-transparent bm-body-m text-bm-ink outline-none"
        }
      />
    </span>
  );
};

export const AddedTag = ({ name, onRemove }) => (
  <span className="inline-flex h-[22px] shrink-0 items-center gap-1 rounded-full border border-bm-strong bg-white pl-[9px] pr-[6px] bm-label-xs text-bm-ink">
    Added
    <button type="button" onClick={onRemove} aria-label={`Remove ${name} from the roster`} className="text-[10px] leading-none text-bm-tertiary hover:text-bm-ink">
      ✕
    </button>
  </span>
);

// The player's name: a button to their profile once they are registered.
// A table row truncates it (the full name on hover); a card wraps it.
export const PlayerName = ({ row, onOpenProfile, wrap = false }) => {
  const fit = wrap ? "break-words" : "truncate";
  const title = row.playerName || undefined;
  if (row.isRegistered && !!row.email) {
    return (
      <button type="button" title={title} onClick={() => onOpenProfile(row)} className={`${fit} text-left bm-h-xs text-bm-ink hover:underline`}>
        {row.playerName}
      </button>
    );
  }
  return (
    <span title={title} className={`${fit} bm-h-xs text-bm-ink`}>
      {row.playerName || EMPTY}
    </span>
  );
};

// What a score still on an older season says while a newer season's first
// games are not yet enough to move it (the stats doc's season_transition:
// {season, nextSeason, nextSeasonGames, needed}): "Scored on the 2025-26
// season until 2026-27 has 8 games (4 so far)." Null without a whole note.
export const seasonTransitionText = (note) => {
  if (!note || typeof note !== "object") return null;
  const { season, nextSeason, nextSeasonGames, needed } = note;
  if (!season || !nextSeason || !(Number(needed) > 0)) return null;
  return `Scored on the ${season} season until ${nextSeason} has ${needed} games (${Number(nextSeasonGames) || 0} so far).`;
};

// The PS chip's hover text: the note when the row carries one, else the
// season the stats are from.
export const previousSeasonTitle = (row) =>
  seasonTransitionText(row?.seasonTransition) ||
  (row?.seasonYear ? `Stats from the ${row.seasonYear} season, no current-season data yet` : "Stats from the previous season");

// The scores show out of 10 (fmtScore): bare in a table, whose header says
// "(out of 10)", and with "/10" on a card (`suffix`), whose label does not.
// `className` is the type style: Numeric/M in a table, Label/L on a card.
export const SportScore = ({ row, className = "bm-num-m", suffix = false }) => (
  <span className="inline-flex items-center gap-1">
    <span className={`${className} text-bm-ink`}>{fmtScore(row.sportScore, { suffix })}</span>
    {row.isPreviousSeason && (
      <span title={previousSeasonTitle(row)}>
        <Badge tone="attention">PS</Badge>
      </span>
    )}
  </span>
);

export const VisibilityScore = ({ row, className = "bm-num-m", suffix = false }) => {
  const score = fmtScore(row.visibilityScore, { suffix });
  return <span className={`${className} ${score === EMPTY ? "text-bm-tertiary" : "text-bm-ink"}`}>{score}</span>;
};

// The words for a revenue share minimum (notes 10/21 D3). The floor is still
// the low end of the player's PerformanceValue (rsFloorSource
// "performanceValue", J2 10/19), but the board names it neutrally: an
// onboarded player's profile shows CacheValue only, so coaches never read
// the PerformanceValue name here. A row valued before PerformanceValue (or
// sent without a floor) floors on its CacheValue, named as such.
export const FLOOR_NAMES = { performanceValue: "value floor", cacheValue: "CacheValue" };

// What raised a revenue share Allocated amount: the player's value floor,
// else his CacheValue (FLOOR_NAMES).
export const revshareFloorName = (row) => (row?.rsFloorSource === "performanceValue" ? FLOOR_NAMES.performanceValue : FLOOR_NAMES.cacheValue);

// "↑" after an Allocated amount the player's own value raised to his
// minimum (`value`: his floor in revenue share, revshareFloorName; his NILx
// value in NIL), saying by how much. Never on a locked amount, and there is
// no down arrow.
const Raised = ({ lift, value }) => {
  const text = `Raised ${fmtMoney2(lift)} to meet this player's ${value}`;
  return (
    <span role="img" aria-label={text} title={text} className="shrink-0 font-bold text-bm-positive">
      ↑
    </span>
  );
};

// The revenue share Allocated amount, which the coach can lock. Team and
// All players beside it are plain amounts (Money).
export const AllocatedPayout = ({ row, onLock, disabled, card = false }) => (
  <PayoutInput
    value={row.allocated}
    locked={row.rsLocked}
    name={row.playerName}
    card={card}
    disabled={disabled}
    onCommit={(v) => onLock("rs", rowId(row), v)}
    trail={row.rsFloored && !row.rsLocked ? <Raised lift={row.rsLift} value={revshareFloorName(row)} /> : null}
  />
);

// The NIL Allocated amount, which the coach can lock like revenue share's.
// The NIL payout beside it is the recommended split, a plain amount.
export const NilAllocated = ({ row, onLock, disabled, card = false }) => (
  <PayoutInput
    value={row.nilAllocated}
    locked={row.nilLocked}
    name={row.playerName}
    label="NIL allocated amount"
    card={card}
    disabled={disabled}
    onCommit={(v) => onLock("nil", rowId(row), v)}
    trail={row.nilFloored && !row.nilLocked ? <Raised lift={row.nilLift} value="NILx value" /> : null}
  />
);
